import { formatJobNumber } from '@fieldops/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { addressLine, formatDate, money } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { ErrorState, SkeletonRows, humanise } from '../ui.js'
import { ContactsPane } from './ContactsPane.js'
import { WhosWho } from './WhosWho.js'
import { NoSitePanel } from './NoSitePanel.js'
import { Panel, PanelHeading, RailFact, Tag } from './panes.js'
import { SiteAddressPane } from './SiteAddressPane.js'
import { SiteKnowledgePane } from './SiteKnowledgePane.js'
import type { Job } from './types.js'

/**
 * Who the job is for, where it is, and who we talk to about it.
 *
 * Client and site used to be two tabs that each repeated the other. This tab
 * is two columns: the people on the job — each of them once — and what has
 * been finished for the client before, beside a rail of the property, how to
 * get in, and the client's own facts (`ClientRail`). The property's own
 * history already sits under the activity feed. Decision 84.
 */
export function ClientPane({
  job,
  canEdit,
  isSavingContact,
  onAddContact,
  onUpdateContact,
  onSetBilling,
  onRemoveContact,
}: {
  job: Job
  canEdit: boolean
  isSavingContact: boolean
  onAddContact: (contactId: string) => void
  onUpdateContact: (input: {
    id: string
    roles?: ('JOB' | 'SITE' | 'OTHER')[]
    label?: string | null
    receivesComms?: boolean
  }) => void
  onSetBilling: (jobContactId: string) => void
  onRemoveContact: (id: string) => void
}) {
  return (
    <>
      <WhosWho job={job} />
      <ContactsPane
        job={job}
        canEdit={canEdit}
        isSaving={isSavingContact}
        onAddContact={onAddContact}
        onUpdateContact={onUpdateContact}
        onSetBilling={onSetBilling}
        onRemoveContact={onRemoveContact}
      />

      <ClientHistory job={job} />
    </>
  )
}

/**
 * The Client tab's side rail: where the job is, how to get in, and who it is
 * for, as three blocks divided by a rule rather than three cards.
 */
export function ClientRail({ job }: { job: Job }) {
  const site = job.site

  return (
    <div className="flex flex-col divide-y divide-ink-100 [&>*]:py-5 [&>*:first-child]:pt-0 [&>*:last-child]:pb-0">
      <div>{site ? <SiteAddressPane job={job} site={site} /> : <NoSitePanel job={job} />}</div>
      {site ? (
        <div>
          <SiteKnowledgePane site={site} />
        </div>
      ) : null}
      <div>
        <ClientFacts job={job} />
      </div>
    </div>
  )
}

/**
 * The client's own facts. Who to ring is a person, on the left; an email and
 * phone stored on the company are not drawn (decision 85).
 */
function ClientFacts({ job }: { job: Job }) {
  const client = job.client

  return (
    <div className="flex flex-col gap-3">
      <PanelHeading
        title="Client"
        action={
          <Link
            to={`/clients/${client.id}`}
            aria-label="Open client"
            className="text-xs font-semibold text-brand-600 hover:text-brand-700"
          >
            Open <span aria-hidden="true">→</span>
          </Link>
        }
      />
      <p className="text-[14px] leading-snug font-semibold text-ink-900">{client.name}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-1.5 text-[12.5px]">
        <RailFact label="Type">{humanise(client.type)}</RailFact>
        <RailFact label="Terms">
          {client.paymentTermsDays === 0 ? 'On the day' : `${client.paymentTermsDays} days`}
        </RailFact>
        <RailFact label="Tax no.">{client.taxNumber ?? '—'}</RailFact>
        <RailFact label="Billing">{addressLine(client.billingAddress)}</RailFact>
      </dl>
      {client.isOnCreditHold ? (
        <div>
          <Tag tone="red">On credit hold</Tag>
        </div>
      ) : null}
    </div>
  )
}

/**
 * What has been finished for this client before.
 *
 * By client rather than by site, which is the difference between this and the
 * history under the activity feed: a commercial client with nine tenancies is
 * one relationship and nine addresses, and the question on this tab is what the
 * relationship looks like.
 */
function ClientHistory({ job }: { job: Job }) {
  const trpc = useTRPC()
  const jobHref = useJobHref()

  const query = useQuery(
    trpc.jobs.list.queryOptions({
      clientId: job.client.id,
      tab: 'COMPLETED',
      sort: 'createdAt',
      dir: 'desc',
      perPage: 25,
    }),
  )

  if (query.error) {
    return (
      <Panel>
        <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
      </Panel>
    )
  }

  if (query.isPending) {
    return (
      <Panel>
        <SkeletonRows rows={4} />
      </Panel>
    )
  }

  const others = query.data.items.filter((row) => row.id !== job.id)

  return (
    <Panel padded={false}>
      <div className="border-b border-ink-100 px-4 py-3.5">
        <PanelHeading
          title="Job history"
          hint={others.length === 0 ? 'Nothing finished for them yet' : undefined}
        />
      </div>
      <ul className="flex flex-col">
        {others.map((row) => (
          <li key={row.id} className="border-b border-ink-100 last:border-b-0">
            <Link
              to={jobHref(row.id)}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-ink-50"
            >
              <span className="shrink-0 font-mono text-[12px] text-ink-500">
                {formatJobNumber(row)}
              </span>
              {/* Where, then what. This list spans one client's properties, so
                  the address is what separates two rows; the category is the
                  tiebreak. Nothing names a job (decision 63), and this column
                  read "Untitled" on every row before it. */}
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-ink-900">
                {row.site?.name ??
                  (row.site ? addressLine(row.site.address) : null) ??
                  row.category?.name ??
                  'Not categorised'}
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-ink-500">
                {formatDate(row.completedAt ?? row.createdAt)}
              </span>
              <span className="shrink-0 text-[12.5px] font-semibold tabular-nums text-ink-900">
                {row.totalIncTax ? money(row.totalIncTax) : '—'}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  )
}
